import { NgClass } from '@angular/common';
import { Component, computed, ElementRef, forwardRef, Input, signal, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatErrorMessagesDirective, UppercaseDirective } from 'app/shared/directives';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    TranslateModule,
    MatIcon,
    MatAutocompleteModule,
    MatOptionModule,
    MatErrorMessagesDirective,
    MatInputModule,
    MatButtonModule,
    UppercaseDirective,
    NgClass,
  ],
})
export class AutocompleteComponent<
  T extends {
    disabled?: boolean;
    sublabel?: string;
  },
> implements ControlValueAccessor
{
  @Input() public form!: FormGroup; // form group to which the input belongs
  @Input() public controlName!: string; // name of the control in the form group
  @Input() public valueProperty!: keyof T; // default value property should be in most cases id
  @Input() public displayProperties: (keyof T)[] = []; // if we want to show more than one property as label (trailerNumber + SCAC for example)
  @Input() public requireSelection = false; // if we should allow new addition to the options (on trailer flow for example)
  @Input() public onlyUppercase = false; // if we should convert the input to uppercase
  @Input() public aria!: string; // aria label for the input
  @Input() public label!: string; // Placeholder for the input
  @Input() public placeholder!: string; // Placeholder for the input
  @Input() public hint?: {
    show: boolean;
    icon?: string;
    message: string;
    colorClass?: string;
  }; // Hint options

  @Input() public set options(value: T[]) {
    this.originalOptions.set(value);
    const ctrl = this.inputControl;
    if (ctrl?.value != null) {
      ctrl.setValue(ctrl.value);
    }
  } // options to be shown in the list

  public originalOptions = signal<T[]>([]);
  public filterSignal = signal<string>('');

  public filteredOptions = computed(() => {
    const filterText = this.filterSignal().toLowerCase();

    if (!filterText) {
      return this.originalOptions();
    }

    return this.originalOptions().filter((option: T) => {
      const combinedLabel =
        this.displayProperties && this.displayProperties.length
          ? this.displayProperties.map((prop) => String(option[prop])).join(' ')
          : '';
      return combinedLabel.toLowerCase().includes(filterText);
    });
  });

  public value: unknown;

  @ViewChild('input') public input!: ElementRef<HTMLInputElement>;

  public get inputControl(): AbstractControl | null {
    return this.form.get(this.controlName);
  }

  public filter(): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this.filterSignal.set(filterValue);
  }

  public registerOnChange(onChange: (value: unknown) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public writeValue(value: T | null): void {
    if (value == null) {
      this.value = null;
      this.inputControl?.setValue(null);
      return;
    }

    const foundOption = this.originalOptions().find((opt) => String(opt[this.valueProperty]) === String(value));

    if (foundOption) {
      this.value = value;
      this.inputControl?.setValue(value);
    } else {
      this.value = null;
      this.inputControl?.setValue(null);
    }
  }

  public onChange = (value: unknown): void => {
    this.value = value;
  };

  public onTouched = (): void => {
    console.log(`Form control touched`);
  };

  public displayOptionsFn = (option?: T | string | null): string => {
    if (!this.originalOptions().length) {
      return '';
    }
    if (option === null && this.form.dirty) {
      const nullOption = this.originalOptions().find((opt) => opt[this.valueProperty] === null);
      return nullOption ? this.formatDisplay(nullOption) : '';
    }

    if (!option) return '';
    if (typeof option === 'string' || typeof option === 'number') {
      const foundOption = this.originalOptions().find((opt) => String(opt[this.valueProperty]) === String(option));
      if (foundOption) {
        return this.formatDisplay(foundOption);
      }
      return String(option);
    }
    return this.formatDisplay(option);
  };

  private formatDisplay(option: T): string {
    if (this.displayProperties && this.displayProperties.length > 0) {
      const first = String(option[this.displayProperties[0]]);
      if (this.displayProperties.length > 1) {
        const rest = this.displayProperties.slice(1).map((prop) => String(option[prop]));
        return `${first} (${rest.join(', ')})`;
      }
      return first;
    }
    const fallback = option[this.valueProperty];
    return fallback != null ? String(fallback) : '';
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedOption: T = event.option.value as T;

    this.value = selectedOption;
    this.inputControl?.setValue(this.value);
    this.onChange(this.value);
  }

  public onOpened(): void {
    if (!this.inputControl?.value) {
      this.filterSignal.set('');
    }
  }

  public clearInput(): void {
    this.inputControl?.setValue(null);
    this.inputControl?.updateValueAndValidity();
  }
}
